import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { StoreContext } from "components/StoreContext";
import { AuthContext } from "components/Auth";
import { debug, states } from "helpers";

const CartContext = React.createContext();
const { Provider, Consumer } = CartContext;

const CartProvider = props => {
  const { children } = props;
  const { config, store, StoreAPI } = useContext(StoreContext);
  const { authorized: loggedIn } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const defaultCustomer = {
    hStreamId: null,
    username: null,
    firstName: "",
    lastName: "",
    emailAddress: null,
    phoneNumber: null,
    znodeUserId: null,
    znodeLoginUsername: null,
    hlcStudentId: null,
    hlcOrganizationId: null,
    hlcInstitutionId: null,
    emailConfirmed: null,
    personResourceId: null
  };
  const [customer, setCustomer] = useState(defaultCustomer);
  const defaultCustomerAddress = {
    addressId: null,
    customerId: null,
    accountId: null,
    displayName: null,
    address1: null,
    cityName: null,
    stateName: null,
    postalCode: null,
    countryName: null,
    isBilling: null,
    isActive: null,
    isShipping: null,
    isDefaultBilling: null,
    isDefaultShipping: null
  };

  const [customerAddress, setCustomerAddress] = useState(
    defaultCustomerAddress
  );

  const defaultCart = {
    loaded: false,
    update: true,
    ready: false,
    merge: false,
    user: null,
    cookie: localStorage.getItem("cart"),
    items: [],
    count: 0,
    subtotal: 0,
    total: 0,
    salesTax: 0,
    totalDiscount: 0,
    promotionalDiscount: 0,
    promotionCouponCodes: [],
    appliedPromo: []
  };

  const [cart, setCart] = useState(defaultCart);
  const history = useHistory();
  const defaultErrorMessage = "Sorry, there was a problem updating your cart.";

  const getCustomerAddress = customerId => {
    setLoading(true);
    setError(null);
    StoreAPI.get(`customers/${customerId}/addresses`)
      .then(response => {
        debug(response);
        setCustomerAddress(response.data);
      })
      .catch(err => {
        debug(err);
        const message = `There was a problem getting the customer's address.`;
        debug("Customers API Error Message:", message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const createCustomerAddress = (customerId, billing) => {
    setError(null);
    const data = {
      displayName: billing.displayName,
      address1: billing.address,
      cityName: billing.city,
      stateName: billing.stateName,
      stateCode: states[billing.stateName],
      postalCode: billing.zip,
      countryName: "US",
      isBilling: true,
      isActive: true,
      isShipping: true,
      isDefaultBilling: true,
      isDefaultShipping: true
    };
    StoreAPI.post(`customers/${customerId}/addresses`, data)
      .then(response => {
        debug(response);
        getCustomerAddress(customerId);
      })
      .catch(err => {
        debug(err);
        const message = `There was a problem creating the customer's address.`;
        debug("Customers API Error Message:", message);
        setError(message);
      });
  };

  const updateCustomerAddress = (customerId, addressId, billing) => {
    let request = {
      method: "put",
      url: `customers/${customerId}/addresses/${addressId}`,
      data: {
        displayName: billing.displayName,
        address1: billing.address,
        cityName: billing.city,
        stateName: billing.stateName,
        stateCode: states[billing.stateName],
        postalCode: billing.zip,
        countryName: billing.countryName,
        isBilling: true,
        isActive: true,
        isShipping: true,
        isDefaultBilling: true,
        isDefaultShipping: true
      }
    };

    if (window.navigator.userAgent.indexOf("Trident/7.0") > -1) {
      request = {
        method: "post",
        url: `customers/${customerId}/addresses/${addressId}/UpdateAddress`,
        data: {
          displayName: billing.displayName,
          address1: billing.address,
          cityName: billing.city,
          stateName: billing.stateName,
          stateCode: states[billing.stateName],
          postalCode: billing.zip,
          countryName: "US",
          isBilling: true,
          isActive: true,
          isShipping: true,
          isDefaultBilling: true,
          isDefaultShipping: true
        }
      };
    }

    setError(null);
    StoreAPI.request(request)
      .then(response => {
        debug(response);
        getCustomerAddress(customerId);
      })
      .catch(err => {
        debug(err);
        const message = `There was a problem updating the customer's address.`;
        debug("Customers API Error Message:", message);
        setError(message);
      });
  };

  const createCustomer = () => {
    setLoading(true);
    setError(null);
    StoreAPI.request({
      headers: {
        ...StoreAPI.defaults.headers,
        ...{ "hstm-config-name": `${config.envName}|${config.appId}` }
      },
      method: "post",
      url: "customers"
    })
      .then(response => {
        // debug(response);
        setCustomer(response.data);
        getCustomerAddress(response.data.znodeUserId);
        setCart({
          ...cart,
          ...{
            user: response.data.znodeUserId
          }
        });
      })
      .catch(err => {
        debug(err);
        const message = `There was a problem creating the customer.`;
        debug("Customers API Error Message:", message);
        setError(message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const clearCustomer = () => {
    setCustomer(defaultCustomer);
    setCustomerAddress(defaultCustomerAddress);
  };

  const checkForActiveItemsInCart = () => {
    setLoading(true);
    setError(null);
    setCart({ ...cart, ...{ ready: false } });

    const activePurchasesSkus = [];
    cart.items.forEach(item => activePurchasesSkus.push(item.sku));

    StoreAPI.post(`orders/active?userId=${customer.znodeUserId}`, {
      ProductSKUs: activePurchasesSkus
    })
      .then(response => {
        const { activePurchases, hasUserActivePurchases } = response.data;
        const message = (
          <div>
            <p>
              We currently don&apos;t allow users to purchase more than one of
              the same product if the product is still active. Please remove the
              following item(s) and try again.
            </p>
            <ul>
              {activePurchases.map(item => (
                <li key={item.sku}>{item.productName}</li>
              ))}
            </ul>
          </div>
        );

        if (hasUserActivePurchases) {
          setError(message);
        } else {
          history.push("/checkout");
        }
      })
      .catch(response => {
        const message =
          response && response.title ? response.title : defaultErrorMessage;
        debug("Cart API Error Message:", message);
        setError(message);
      })
      .finally(() => {
        setLoading(false);
        setCart({ ...cart, ...{ ready: true } });
      });
  };

  const getCartCount = (
    UserID = customer.znodeUserId,
    cookie = cart.cookie
  ) => {
    const params = {
      cookieMappingId: cookie
    };

    if (loggedIn) {
      params.UserID = UserID;
    }

    setLoading(true);
    setError(null);

    StoreAPI.get("cart/cartitems/count", { params })
      .then(response => {
        const newCart = {
          ...cart,
          ...{
            count: response.data.cartItemsCount
          }
        };
        setCart(newCart);
      })
      .catch(err => {
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getCart = (UserID = customer.znodeUserId) => {
    debug("Getting items in cart ID:", cart.cookie);
    setLoading(true);
    setError(null);

    const params = {
      cookieMappingId: cart.cookie
    };

    if (loggedIn) {
      params.UserID = UserID;
    }

    StoreAPI.get("cart", { params })
      .then(response => {
        const newCart = {
          ...cart,
          ...{
            loaded: true,
            update: false,
            ready: true,
            user: response.data.userId,
            cookie: response.data.cookieMappingId,
            items: response.data.shoppingCartItems.reverse(),
            count: response.data.shoppingCartItems.length,
            totalDiscount: response.data.totalDiscount,
            subtotal: response.data.subTotal,
            total: response.data.total
          }
        };

        if (
          newCart.appliedPromo.length === 0 &&
          sessionStorage.getItem("promotions")
        ) {
          newCart.appliedPromo = [sessionStorage.getItem("promotions")];
        }

        setCart(newCart);
        localStorage.setItem("cart", response.data.cookieMappingId);

        debug(
          "Finished getting items in cart ID:",
          response.data.cookieMappingId
        );
      })
      .catch(err => {
        // debug(err);
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const addCart = (sku, pid, retail, UserID = customer.znodeUserId) => {
    const count = cart.count + 1;
    const found = cart.items.find(o => o.sku === sku);

    if (found) {
      debug("Cart: Item already in cart");
    } else {
      debug("Add item:", sku, " to cart ID:", cart.cookie);
      setLoading(true);
      setError(null);
      setCart({ ...cart, ...{ ready: false, count } });

      const data = {
        Sku: sku,
        ProductId: pid,
        RetailPrice: retail,
        Quantity: 1,
        CookieMappingId: cart.cookie
      };

      if (loggedIn) {
        data.UserID = UserID;
      }

      StoreAPI.post("cart", data)
        .then(response => {
          debug(
            "Finished adding item:",
            sku,
            " to cart ID:",
            response.data.cookieMappingId
          );
          // debug(response);
          setCart({
            ...cart,
            ...{
              update: true,
              count,
              cookie: response.data.cookieMappingId
            }
          });
          localStorage.setItem("cart", response.data.cookieMappingId);
          getCartCount(UserID, response.data.cookieMappingId);
        })
        .catch(err => {
          // debug(err);
          const { response } = err;
          const message =
            response && response.title ? response.title : defaultErrorMessage;
          debug("Cart API Error Message:", message);
          setLoading(false);
          setError(message);
        });
    }
  };

  const removeCart = (item, UserID = customer.znodeUserId) => {
    debug("Removing saved item id:", item, " from cart ID:", cart.cookie);

    const count = cart.count - 1;
    const nextCart = { ...cart, ...{ ready: false, count } };

    setLoading(true);
    setError(null);
    setCart({ ...cart, ...{ ready: false, count } });

    let request = {
      method: "delete",
      url: "cart",
      data: {
        SavedCartLineItemIds: [item],
        CookieMappingId: cart.cookie
      }
    };

    if (window.navigator.userAgent.indexOf("Trident/7.0") > -1) {
      request = {
        method: "post",
        url: "cart/DeleteItemFromCart",
        data: {
          SavedCartLineItemIds: [item],
          CookieMappingId: cart.cookie
        }
      };
    }

    if (loggedIn) {
      request.data.UserID = UserID;
    }

    StoreAPI.request(request)
      .then(response => {
        debug(response);
        debug(
          "Finished removing saved item id:",
          item,
          " from cart ID:",
          cart.cookie
        );
        setCart({ ...nextCart, ...{ update: true } });
        getCartCount();
      })
      .catch(err => {
        debug(err);
        const { response } = err;
        const message =
          response && response.title ? response.title : defaultErrorMessage;
        debug("Cart API Error Message:", message);
        setLoading(false);
        setError(message);
      });
  };

  const calculateCart = (options = {}) => {
    setLoading(true);
    setError(null);

    const data = {
      UserId: customer.znodeUserId,
      CookieMappingId: cart.cookie,
      PromotionCouponCode: [],
      shoppingCartItems: []
    };

    cart.items.forEach(item => {
      data.shoppingCartItems.push({
        quantity: item.quantity,
        sku: item.sku,
        cartLineItemId: item.omsSavedcartLineItemId,
        retailPrice: item.retailPrice
      });
    });

    if (options.billing) {
      data.BillingAddress = {
        AddressId: customerAddress.addressId,
        Address1: options.billing.address,
        CityName: options.billing.city,
        StateName: options.billing.stateName,
        StateCode: options.billing.stateCode,
        PostalCode: options.billing.zip,
        CountryName: "US"
      };
    }

    if (options.promosToApply) {
      data.PromotionCouponCode = options.promosToApply; // array
    }

    StoreAPI.post("cart/calculate", data)
      .then(response => {
        // debug(response);
        const newCart = {
          ...cart,
          ...{
            loaded: true,
            merge: false,
            user: response.data.userId,
            subtotal: response.data.subTotal,
            total: response.data.total,
            salesTax: response.data.salesTax,
            promotionalDiscount: response.data.promotionalDiscount,
            totalDiscount: response.data.totalDiscount,
            promotionCouponCodes: response.data.promotionCouponCodes,
            appliedPromo: []
          }
        };

        newCart.promotionCouponCodes.forEach(promo => {
          // works for one at a time
          if (promo.couponValid) {
            newCart.appliedPromo = [promo.code];
            sessionStorage.setItem("promotions", newCart.appliedPromo);
          }
        });

        setCart(newCart);
      })
      .catch(err => {
        debug(err);
        const message = `There was a problem calculating the sales tax.`;
        debug("Cart Calculate API Error Message:", message);
        setError(message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const clearCart = () => {
    localStorage.removeItem("cart");
    sessionStorage.removeItem("promotions");
    const newCart = {
      ...defaultCart,
      ...{
        update: false,
        user: cart.user,
        cookie: localStorage.getItem("cart")
      }
    };
    setCart(newCart);
  };

  const mergeCart = () => {
    const UserID = cart.user;
    const item = cart.items[0].omsSavedcartLineItemId;

    StoreAPI.get(`cart/mergeguestusercart?UserId=${UserID}&CartItemId=${item}`)
      .then(response => {
        const data = response.data.mergedShoppingCart;
        const newCart = {
          ...cart,
          ...{
            loaded: true,
            ready: true,
            update: false,
            merge: false,
            user: data.userId,
            cookie: data.cookieMappingId,
            items: data.shoppingCartItems,
            count: data.shoppingCartItems.length,
            subtotal: data.subTotal,
            total: data.total,
            salesTax: data.salesTax,
            totalDiscount: data.totalDiscount,
            promotionalDiscount: 0
          }
        };
        setCart(newCart);
        localStorage.setItem("cart", data.cookieMappingId);
      })
      .catch(err => {
        debug(err);
        const message = `There was a problem saving the items already in your cart.`;
        setError(message);
      });
  };

  const getCustomer = () => {
    setLoading(true);
    setError(null);
    StoreAPI.get("customers")
      .then(response => {
        setCustomer(response.data);
        getCustomerAddress(response.data.znodeUserId);
        const newCart = {
          ...cart,
          ...{
            ready: false,
            user: response.data.znodeUserId
          }
        };

        if (newCart.items.length > 0) {
          newCart.merge = true;
        } else {
          newCart.update = true;
        }

        setCart(newCart);
        setLoading(false);

        // if (newCart.items.length > 0) {
        //   const item = newCart.items[0].omsSavedcartLineItemId;
        //   mergeCart(item, response.data.znodeUserId);
        // } else {
        //   getCart(response.data.znodeUserId);
        // }
      })
      .catch(err => {
        debug(err);
        createCustomer();
      });
  };

  useEffect(() => {
    if (store.loaded) {
      // cart can't update
      if (!cart.cookie || !cart.user) {
        setCart({
          ...cart,
          ...{
            update: false
          }
        });
      }

      // cart requires update
      if (cart.update && (cart.cookie || cart.user)) {
        getCart();
      }

      // loggedin, no cookie, no customer
      // loggedin, no customer information, but waiting to see if there are items to merge
      if (
        loggedIn &&
        (!cart.cookie || cart.ready) &&
        !customer.znodeUserId &&
        !cart.update
      ) {
        getCustomer();
      }

      // merge
      if (cart.merge) {
        // console.log("merging");
        const newCart = {
          ...cart,
          ...{
            merge: false
          }
        };
        setCart(newCart);
        mergeCart();
      }
    }

    // TODO: Check on status of single call useEffect issue: https://github.com/facebook/react/issues/15865
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    store.loaded,
    cart.cookie,
    cart.update,
    cart.user,
    cart.ready,
    cart.loaded,
    cart.merge,
    loggedIn
  ]);

  return (
    <Provider
      value={{
        loading,
        error,
        customer,
        customerAddress,
        createCustomerAddress,
        updateCustomerAddress,
        clearCustomer,
        cart,
        createCustomer,
        onCartAdd: addCart,
        onCartRemove: removeCart,
        calculateCart,
        clearCart,
        onCartCheckout: checkForActiveItemsInCart,
        setCart,
        setLoading,
        setError
      }}
    >
      {children}
    </Provider>
  );
};

CartProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export { CartProvider, Consumer as CartConsumer, CartContext };
